import { useState } from "react";
import { Calculator, TrendingUp, Shield, Clock, CalendarIcon, Download, FileText, CreditCard, BarChart3 } from "lucide-react";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "@/hooks/use-toast";

// Types
interface PeriodRow {
  Period: number;
  PaymentDate?: string;
  OpeningBalance: number;
  Payment: number;
  Interest: number;
  Principal: number;
  ClosingBalance: number;
}

interface CalcResults {
  payment: number;
  totalInterest: number;
  totalPayment: number;
  schedule: PeriodRow[];
  numberOfPayments: number;
}

type CalculationMode = "reducing" | "flat";
type TenureUnit = "Months" | "Years";

const CalculatorPage = () => {
  // Advanced calculator state
  const [loanAmount, setLoanAmount] = useState("");
  const [annualRate, setAnnualRate] = useState("");
  const [tenureValue, setTenureValue] = useState("");
  const [tenureUnit, setTenureUnit] = useState<TenureUnit>("Months");
  const [startDate, setStartDate] = useState<Date>();
  const [calcMode, setCalcMode] = useState<CalculationMode>("reducing");
  
  // Quick calculator state (loan products)
  const [quickLoanAmount, setQuickLoanAmount] = useState<string>("");
  const [quickLoanPeriod, setQuickLoanPeriod] = useState<string>("12");
  const [quickLoanType, setQuickLoanType] = useState<string>("normal");
  const [customInterestRate, setCustomInterestRate] = useState<string>("12");
  const [quickMonthlyPayment, setQuickMonthlyPayment] = useState<number | null>(null);
  const [quickTotalPayment, setQuickTotalPayment] = useState<number | null>(null);
  const [quickTotalInterest, setQuickTotalInterest] = useState<number | null>(null);
  
  // Results state
  const [results, setResults] = useState<CalcResults | null>(null);
  const [showSchedule, setShowSchedule] = useState(false);

  // Loan products with their specific rates and terms
  const loanProducts = {
    normal: {
      name: "Development Loan",
      interestRate: 12, // 1% per month = 12% p.a.
      maxPeriod: 96,
      description: "Up to 3x your savings limit for 96 months"
    },
    emergency: {
      name: "Emergency Loan",
      interestRate: 18, // 1.5% per month = 18% p.a.
      maxPeriod: 12,
      description: "For unexpected urgent needs"
    },
    education: {
      name: "Education Loan",
      interestRate: 15, // 1.25% per month = 15% p.a.
      maxPeriod: 12,
      description: "For school and university fees"
    },
    device: {
      name: "Salary Advance",
      interestRate: 24, // 2% per month = 24% p.a.
      maxPeriod: 3,
      description: "Up to KSh 150,000 for 3 months at 2% p.m."
    },
    custom: {
      name: "Custom Calculator",
      interestRate: 12,
      maxPeriod: 198,
      description: "Set your own interest rate and terms"
    }
  };

  // Quick calculator functions
  const generateMonthOptions = () => {
    const maxMonths = loanProducts[quickLoanType as keyof typeof loanProducts].maxPeriod;
    const options = [];
    
    // Add common short-term options
    for (let i = 1; i <= Math.min(12, maxMonths); i++) {
      options.push(i);
    }
    
    // Add yearly increments up to the max
    for (let i = 18; i <= maxMonths; i += 6) {
      if (i <= maxMonths) {
        options.push(i);
      }
    }
    
    return options;
  };

  const calculateQuickLoanPayment = () => {
    const principal = parseFloat(quickLoanAmount);
    const months = parseInt(quickLoanPeriod);
    const selectedProduct = loanProducts[quickLoanType as keyof typeof loanProducts];
    
    // Use custom interest rate for custom calculator, otherwise use product rate
    let annualRate = selectedProduct.interestRate;
    if (quickLoanType === "custom") {
      const customRate = parseFloat(customInterestRate);
      if (customRate > 0 && customRate <= 50) {
        annualRate = customRate;
      }
    }

    if (!principal || !months || !annualRate || principal <= 0 || months <= 0 || annualRate < 0) {
      setQuickMonthlyPayment(null);
      setQuickTotalPayment(null);
      setQuickTotalInterest(null);
      return;
    }

    // Convert annual rate to monthly rate
    const monthlyRate = annualRate / 100 / 12;
    
    // Calculate monthly payment using loan formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const total = payment * months;
    const interest = total - principal;
    
    setQuickMonthlyPayment(payment);
    setQuickTotalPayment(total);
    setQuickTotalInterest(interest);
  };

  // Auto-calculate when loan type changes
  const handleQuickLoanTypeChange = (value: string) => {
    setQuickLoanType(value);
    const selectedProduct = loanProducts[value as keyof typeof loanProducts];
    
    // Reset period if it exceeds the new product's max period
    if (parseInt(quickLoanPeriod) > selectedProduct.maxPeriod) {
      setQuickLoanPeriod("12");
    }
  };

  // Advanced calculation functions
  const validateInputs = (): string | null => {
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(annualRate);
    const tenure = parseFloat(tenureValue);
    
    if (!principal || principal <= 0 || principal < 1000) {
      return "Please enter a valid loan amount (minimum KES 1,000)";
    }
    
    if (!rate || rate < 0 || rate >= 100) {
      return "Please enter a valid interest rate (0-99.99%)";
    }
    
    if (!tenure || tenure <= 0) {
      return "Please enter a valid loan tenure";
    }
    
    const termMonths = tenureUnit === "Years" ? tenure * 12 : tenure;
    if (termMonths > 600) {
      return "Maximum loan term is 600 months (50 years)";
    }
    
    return null;
  };

  const calculateReducingBalanceSchedule = (
    principal: number,
    annualRatePct: number,
    termMonths: number,
    startDate?: Date
  ): CalcResults => {
    const periodicRate = annualRatePct / 100 / 12;
    let monthlyPayment: number;
    
    if (periodicRate === 0) {
      monthlyPayment = principal / termMonths;
    } else {
      monthlyPayment = principal * (periodicRate * Math.pow(1 + periodicRate, termMonths)) / 
                     (Math.pow(1 + periodicRate, termMonths) - 1);
    }
    
    const schedule: PeriodRow[] = [];
    let openingBalance = principal;
    let totalInterestPaid = 0;
    
    for (let period = 1; period <= termMonths; period++) {
      const interestPayment = openingBalance * periodicRate;
      let principalPayment = monthlyPayment - interestPayment;
      let payment = monthlyPayment;
      
      // Handle final period rounding
      if (period === termMonths) {
        principalPayment = openingBalance;
        payment = principalPayment + interestPayment;
      }
      
      const closingBalance = Math.max(0, openingBalance - principalPayment);
      
      let paymentDate: string | undefined;
      if (startDate) {
        const date = new Date(startDate);
        date.setMonth(date.getMonth() + period - 1);
        paymentDate = format(date, "dd MMM yyyy");
      }
      
      schedule.push({
        Period: period,
        PaymentDate: paymentDate,
        OpeningBalance: openingBalance,
        Payment: payment,
        Interest: interestPayment,
        Principal: principalPayment,
        ClosingBalance: closingBalance
      });
      
      totalInterestPaid += interestPayment;
      openingBalance = closingBalance;
    }
    
    const totalPayment = principal + totalInterestPaid;
    
    return {
      payment: monthlyPayment,
      totalInterest: totalInterestPaid,
      totalPayment,
      schedule,
      numberOfPayments: termMonths
    };
  };

  const calculateFlatRateSchedule = (
    principal: number,
    annualRatePct: number,
    termMonths: number,
    startDate?: Date
  ): CalcResults => {
    const totalInterest = principal * (annualRatePct / 100) * (termMonths / 12);
    const totalPayment = principal + totalInterest;
    const monthlyPayment = totalPayment / termMonths;
    const interestPerPeriod = totalInterest / termMonths;
    const principalPerPeriod = principal / termMonths;
    
    const schedule: PeriodRow[] = [];
    let openingBalance = principal;
    
    for (let period = 1; period <= termMonths; period++) {
      let paymentDate: string | undefined;
      if (startDate) {
        const date = new Date(startDate);
        date.setMonth(date.getMonth() + period - 1);
        paymentDate = format(date, "dd MMM yyyy");
      }
      
      const closingBalance = openingBalance - principalPerPeriod;
      
      schedule.push({
        Period: period,
        PaymentDate: paymentDate,
        OpeningBalance: openingBalance,
        Payment: monthlyPayment,
        Interest: interestPerPeriod,
        Principal: principalPerPeriod,
        ClosingBalance: Math.max(0, closingBalance)
      });
      
      openingBalance = closingBalance;
    }
    
    return {
      payment: monthlyPayment,
      totalInterest,
      totalPayment,
      schedule,
      numberOfPayments: termMonths
    };
  };

  const handleCalculate = () => {
    const validationError = validateInputs();
    if (validationError) {
      toast({
        title: "Invalid Input",
        description: validationError,
        variant: "destructive"
      });
      return;
    }
    
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(annualRate);
    const tenure = parseFloat(tenureValue);
    const termMonths = tenureUnit === "Years" ? tenure * 12 : tenure;
    
    try {
      let calculationResults: CalcResults;
      
      if (calcMode === "reducing") {
        calculationResults = calculateReducingBalanceSchedule(principal, rate, termMonths, startDate);
      } else {
        calculationResults = calculateFlatRateSchedule(principal, rate, termMonths, startDate);
      }
      
      setResults(calculationResults);
      setShowSchedule(false);
      
      toast({
        title: "Calculation Complete",
        description: `${calcMode === "reducing" ? "Reducing balance" : "Flat rate"} schedule generated successfully.`
      });
    } catch (error) {
      toast({
        title: "Calculation Error",
        description: "An error occurred during calculation. Please check your inputs.",
        variant: "destructive"
      });
    }
  };

  const generateSchedulePdf = async () => {
    if (!results) return;
    
    try {
      const doc = new jsPDF();
      const currentDate = new Date();
      const fileName = `NatCon_SACCO_${calcMode === "reducing" ? "Reducing_Balance" : "Flat_Rate"}_Schedule_${format(currentDate, "yyyyMMdd_HHmmss")}.pdf`;
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(0, 107, 63); // Primary color
      doc.text("NatCon SACCO", 20, 20);
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text(`Loan ${calcMode === "reducing" ? "Amortization" : "Payment"} Schedule`, 20, 30);
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text("For illustration only", 20, 38);
      
      // Loan Summary
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("Loan Summary:", 20, 50);
      
      const summaryData = [
        ["Loan Amount (KES)", formatCurrency(parseFloat(loanAmount))],
        ["Annual Interest Rate (%)", `${annualRate}%`],
        ["Tenure", `${tenureValue} ${tenureUnit.toLowerCase()}`],
        ["Repayment Frequency", "Monthly"],
        ["Calculation Method", calcMode === "reducing" ? "Reducing Balance (Amortized)" : "Flat Rate (Simple)"],
        ["Monthly Payment (KES)", formatCurrency(results.payment)],
        ["Total Interest (KES)", formatCurrency(results.totalInterest)],
        ["Total Payment (KES)", formatCurrency(results.totalPayment)],
        ...(startDate ? [["First Payment Date", format(startDate, "dd MMM yyyy")]] : [])
      ];
      
      autoTable(doc, {
        startY: 55,
        head: [["Item", "Value"]],
        body: summaryData,
        theme: "grid",
        headStyles: { fillColor: [0, 107, 63] },
        margin: { left: 20, right: 20 }
      });
      
      // Schedule Table
      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.text("Payment Schedule:", 20, finalY);
      
      const scheduleHeaders = ["Period", "Opening Balance", "Payment", "Interest", "Principal", "Closing Balance"];
      if (startDate) {
        scheduleHeaders.splice(1, 0, "Payment Date");
      }
      
      const scheduleData = results.schedule.map(row => {
        const rowData = [
          row.Period.toString(),
          formatCurrency(row.OpeningBalance),
          formatCurrency(row.Payment),
          formatCurrency(row.Interest),
          formatCurrency(row.Principal),
          formatCurrency(row.ClosingBalance)
        ];
        
        if (startDate && row.PaymentDate) {
          rowData.splice(1, 0, row.PaymentDate);
        }
        
        return rowData;
      });
      
      autoTable(doc, {
        startY: finalY + 5,
        head: [scheduleHeaders],
        body: scheduleData,
        theme: "striped",
        headStyles: { fillColor: [0, 107, 63] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 8 }
      });
      
      // Footer
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("Estimates only; subject to NatCon SACCO approval.", 20, pageHeight - 20);
      doc.text("NatCon SACCO • www.nca.go.ke", 20, pageHeight - 12);
      
      doc.save(fileName);
      
      toast({
        title: "PDF Downloaded",
        description: "Your loan schedule has been downloaded successfully."
      });
    } catch (error) {
      toast({
        title: "PDF Generation Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('KES', 'KSh');
  };

  const features = [
    {
      icon: Calculator,
      title: "Advanced Calculations",
      description: "Both reducing balance and flat rate calculations with Excel-level accuracy"
    },
    {
      icon: TrendingUp,
      title: "Detailed Schedules",
      description: "Complete amortization schedules showing principal and interest breakdown"
    },
    {
      icon: FileText,
      title: "PDF Export",
      description: "Download professional branded payment schedules for your records"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your financial information is processed securely and never stored"
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            NatCon SACCO Loan Calculator
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Professional loan calculator with reducing balance and flat rate calculations. 
            Generate detailed amortization schedules and download professional PDF reports.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-gradient-primary hover:opacity-90" asChild>
              <a href="#calculator">Start Calculating</a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <a href="/membership">Become a Member</a>
            </Button>
          </div>
        </section>

        {/* Features Grid */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <feature.icon className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Calculator Section */}
        <section id="calculator" className="mb-16">
          <div className="max-w-6xl mx-auto">
            <Card className="shadow-lg">
              <CardHeader className="text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="p-3 rounded-full bg-primary/10">
                    <Calculator className="h-8 w-8 text-primary" />
                  </div>
                  <div className="text-left">
                    <CardTitle className="text-2xl text-foreground">NatCon SACCO Loan Calculator</CardTitle>
                    <p className="text-sm text-muted-foreground">Quick estimates and detailed amortization schedules</p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <Tabs defaultValue="quick" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="quick" className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Quick Loan Calculator
                    </TabsTrigger>
                    <TabsTrigger value="advanced" className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Advanced Calculator
                    </TabsTrigger>
                  </TabsList>

                  {/* Quick Loan Calculator Tab */}
                  <TabsContent value="quick" className="space-y-6">
                    <div className="text-center py-4">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Quick Loan Product Calculator</h3>
                      <p className="text-sm text-muted-foreground">Get instant payment estimates for our loan products</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Input Section */}
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">
                            Loan Type
                          </label>
                          <Select value={quickLoanType} onValueChange={handleQuickLoanTypeChange}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select loan type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="normal">Development Loan - 12% p.a.</SelectItem>
                              <SelectItem value="emergency">Emergency Loan - 18% p.a.</SelectItem>
                              <SelectItem value="education">Education Loan - 15% p.a.</SelectItem>
                              <SelectItem value="device">Salary Advance - 2% p.m.</SelectItem>
                              <SelectItem value="custom">Custom Calculator</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            {loanProducts[quickLoanType as keyof typeof loanProducts].description}
                          </p>
                        </div>

                        {quickLoanType === "custom" && (
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Interest Rate (% per annum)
                            </label>
                            <Input
                              type="number"
                              placeholder="12"
                              min="0.1"
                              max="50"
                              step="0.1"
                              value={customInterestRate}
                              onChange={(e) => setCustomInterestRate(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Maximum rate: 50% per annum
                            </p>
                          </div>
                        )}

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">
                            Loan Amount (KSh)
                          </label>
                          <Input
                            type="number"
                            placeholder="500,000"
                            value={quickLoanAmount}
                            onChange={(e) => setQuickLoanAmount(e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">
                            Loan Period (Months)
                          </label>
                          <Select value={quickLoanPeriod} onValueChange={setQuickLoanPeriod}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select period" />
                            </SelectTrigger>
                            <SelectContent>
                              {generateMonthOptions().map((months) => (
                                <SelectItem key={months} value={months.toString()}>
                                  {months} month{months > 1 ? 's' : ''} {months >= 12 ? `(${(months/12).toFixed(1)} year${months >= 24 ? 's' : ''})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            Max period: {loanProducts[quickLoanType as keyof typeof loanProducts].maxPeriod} months
                          </p>
                        </div>

                        <Button 
                          onClick={calculateQuickLoanPayment}
                          className="w-full bg-gradient-primary hover:opacity-90"
                        >
                          Calculate Payment
                        </Button>
                      </div>

                      {/* Results Section */}
                      <div className="space-y-4">
                        <Card className="bg-secondary/50">
                          <CardContent className="p-6 space-y-4">
                            <div className="text-center">
                              <div className="text-sm text-muted-foreground">Monthly Payment</div>
                              <div className="text-3xl font-bold text-primary">
                                {quickMonthlyPayment ? formatCurrency(quickMonthlyPayment) : "Enter loan details"}
                              </div>
                            </div>
                            
                            {quickTotalPayment && quickTotalInterest && (
                              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground">Total Payment</div>
                                  <div className="text-lg font-semibold">{formatCurrency(quickTotalPayment)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground">Total Interest</div>
                                  <div className="text-lg font-semibold">{formatCurrency(quickTotalInterest)}</div>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground">
                            Need a detailed payment schedule? Use the <strong>Advanced Calculator</strong> tab for full amortization tables and PDF export.
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Advanced Calculator Tab */}
                  <TabsContent value="advanced" className="space-y-6">
                    <div className="text-center py-4">
                      <h3 className="text-lg font-semibold text-foreground mb-2">Advanced Amortization Calculator</h3>
                      <p className="text-sm text-muted-foreground">Detailed schedules with reducing balance and flat rate calculations</p>
                    </div>

                    {/* Calculation Method Toggle */}
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-3">
                        Calculation Method
                      </label>
                      <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-lg">
                        <Button
                          variant={calcMode === "reducing" ? "default" : "ghost"}
                          onClick={() => setCalcMode("reducing")}
                          className="text-sm"
                        >
                          Reducing Balance (Amortized)
                        </Button>
                        <Button
                          variant={calcMode === "flat" ? "default" : "ghost"}
                          onClick={() => setCalcMode("flat")}
                          className="text-sm"
                        >
                          Flat Rate (Simple)
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {calcMode === "reducing" 
                          ? "Interest is charged on the remaining balance; payment stays constant (except final rounding)."
                          : "Interest is calculated on the original principal and spread evenly over the term."
                        }
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Left Column */}
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">
                            Loan Amount (KES) *
                          </label>
                          <Input
                            type="number"
                            placeholder="1,000,000"
                            min="1000"
                            value={loanAmount}
                            onChange={(e) => setLoanAmount(e.target.value)}
                            className="w-full"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Minimum: KES 1,000</p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">
                            Annual Interest Rate (%) *
                          </label>
                          <Input
                            type="number"
                            placeholder="14.00"
                            min="0"
                            max="99.99"
                            step="0.01"
                            value={annualRate}
                            onChange={(e) => setAnnualRate(e.target.value)}
                            className="w-full"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Maximum: 99.99%</p>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Loan Tenure *
                            </label>
                            <Input
                              type="number"
                              placeholder="36"
                              min="1"
                              value={tenureValue}
                              onChange={(e) => setTenureValue(e.target.value)}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Tenure Unit
                            </label>
                            <Select value={tenureUnit} onValueChange={(value: TenureUnit) => setTenureUnit(value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Months">Months</SelectItem>
                                <SelectItem value="Years">Years</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">
                            First Payment Date (Optional)
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !startDate && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {startDate ? format(startDate, "PPP") : <span>Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={startDate}
                                onSelect={setStartDate}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <p className="text-xs text-muted-foreground mt-1">Used for schedule dates in PDF</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Button onClick={handleCalculate} className="flex-1">
                        <Calculator className="mr-2 h-4 w-4" />
                        Calculate
                      </Button>
                      
                      <Button 
                        onClick={generateSchedulePdf}
                        variant="outline" 
                        disabled={!results}
                        className="flex-1"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download PDF
                      </Button>
                    </div>

                    {/* Results Section */}
                    {results && (
                      <div className="space-y-6 border-t border-border pt-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-primary">
                                {formatCurrency(results.payment)}
                              </div>
                              <div className="text-sm text-muted-foreground">Monthly Payment</div>
                            </CardContent>
                          </Card>
                          
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-destructive">
                                {formatCurrency(results.totalInterest)}
                              </div>
                              <div className="text-sm text-muted-foreground">Total Interest</div>
                            </CardContent>
                          </Card>
                          
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-foreground">
                                {formatCurrency(results.totalPayment)}
                              </div>
                              <div className="text-sm text-muted-foreground">Total Payment</div>
                            </CardContent>
                          </Card>
                          
                          <Card>
                            <CardContent className="p-4 text-center">
                              <div className="text-2xl font-bold text-muted-foreground">
                                {results.numberOfPayments}
                              </div>
                              <div className="text-sm text-muted-foreground">Number of Payments</div>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Schedule Toggle */}
                        <div className="text-center">
                          <Button
                            onClick={() => setShowSchedule(!showSchedule)}
                            variant="outline"
                            className="gap-2"
                          >
                            <FileText className="h-4 w-4" />
                            {showSchedule ? "Hide" : "Show"} Payment Schedule
                          </Button>
                        </div>

                        {/* Payment Schedule Table */}
                        {showSchedule && (
                          <div className="border rounded-lg overflow-hidden">
                            <div className="max-h-96 overflow-auto">
                              <Table>
                                <TableHeader className="sticky top-0 bg-muted">
                                  <TableRow>
                                    <TableHead className="text-center">Period</TableHead>
                                    {startDate && <TableHead className="text-center">Payment Date</TableHead>}
                                    <TableHead className="text-right">Opening Balance</TableHead>
                                    <TableHead className="text-right">Payment</TableHead>
                                    <TableHead className="text-right">Interest</TableHead>
                                    <TableHead className="text-right">Principal</TableHead>
                                    <TableHead className="text-right">Closing Balance</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {results.schedule.map((row, index) => (
                                    <TableRow key={index} className="text-sm">
                                      <TableCell className="text-center font-medium">
                                        {row.Period}
                                      </TableCell>
                                      {startDate && (
                                        <TableCell className="text-center">
                                          {row.PaymentDate}
                                        </TableCell>
                                      )}
                                      <TableCell className="text-right font-mono">
                                        {formatCurrency(row.OpeningBalance)}
                                      </TableCell>
                                      <TableCell className="text-right font-mono font-semibold">
                                        {formatCurrency(row.Payment)}
                                      </TableCell>
                                      <TableCell className="text-right font-mono text-destructive">
                                        {formatCurrency(row.Interest)}
                                      </TableCell>
                                      <TableCell className="text-right font-mono text-primary">
                                        {formatCurrency(row.Principal)}
                                      </TableCell>
                                      <TableCell className="text-right font-mono">
                                        {formatCurrency(row.ClosingBalance)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="bg-muted/50 p-4 rounded-lg text-sm text-muted-foreground">
                      <strong>Disclaimer:</strong> This calculator provides estimates for illustration only and does not constitute a loan offer. Terms are subject to NatCon SACCO approval.
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Apply for Your Loan?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Take the next step towards achieving your financial goals. Our team is ready to help you 
            navigate the loan application process and find the perfect solution for your needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-gradient-primary hover:opacity-90" asChild>
              <a href="/contact">Apply Now</a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <a href="/products">View All Loans</a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CalculatorPage;